import { Injectable } from '@angular/core';
import { RemoteDiagnosticsCloudApiMockResponses } from '@vi-iot/client/apis/remote-diagnostics/remote-diagnostics-cloud.mock.mocked-response';
import { Observable, of, Subject, timer } from 'rxjs';
import { delay, map, withLatestFrom } from 'rxjs/operators';

import {
  DiagnosticsStackDeviceMainStructure,
  DiagnosticsStackGenericResponse,
  mockEndpointDelay,
  mockSessionGuid,
  mockSessionId,
  RemoteDiagnosticsHelpersService,
  UseCases,
  UseCaseVddList,
  VddSimulator,
} from '@vi-iot/client';

import { ProductMatrixStructure, BackupSeparator, SetupInfo } from '@store';

import {
  DiagStackGenericResponse,
  DiagStackNode,
  RemoteDiagnosticsCloudApiContract,
  RemoteDiagnosticsGetVddListByComChannelGeneric,
  RemoteDiagnosticsSeesionGuid,
  RemoteDiagnosticsSession,
  RemoteDiagnosticsSessionClosed,
  RemoteDiagnosticsSuccessResponseData,
  RemoteDiagnosticsVciAvailable,
  RemoteDiagnosticsVddRootMetadataResponse,
  RemoteDiagnosticsVddRootStructureResponse,
} from './remote-diagnostics.model';
import { ActivatedRoute } from '@angular/router';

import * as Vdds from './mocked-vdds/index';
import * as LogicalLinkVdds from './mocked-vdd-list-for-LL/index';
import { environment } from '../../../../environments/environment';
import { MockedVdd } from './mocked-vdds/MockedVdd.model';
import { BusTopologyPhaseOne_vitochargeVx3 } from './mocked-topologies/BusTopologyPhaseOne_vitocharge-vx3';
import { LogicalLinkVddList } from './mocked-vdd-list-for-LL/mockedVddList.model';
import { BusTopologyPhaseOne_vitodens300 } from './mocked-topologies/BusTopologyPhaseOne_vitodens-300';
import { BusTopologyPhaseOne_vitovalorPT2 } from './mocked-topologies/BusTopologyPhaseOne_vitovalor-PT2';
import { BusTopologyPhaseOne_vitocal222 } from './mocked-topologies/BusTopologyPhaseOne_vitocal-222';
import { BusTopologyPhaseOne_viair } from './mocked-topologies/BusTopologyPhaseOne_viair';

@Injectable()
export class RemoteDiagnosticsCloudMockApi implements RemoteDiagnosticsCloudApiContract {
  public mockWebSocketResponseAlwaysError = false;
  public mockEndpointDelay = 1000;
  public mockWebSocketDelay = 500;
  private vddWsSimulators: VddSimulator[] = [];

  constructor(
    private activatedRoute: ActivatedRoute,
    private remoteDiagnosticsHelpersService: RemoteDiagnosticsHelpersService
  ) {}

  getDiagStackNodeNext(): Observable<DiagStackNode> {
    return of(RemoteDiagnosticsCloudApiMockResponses.diagStackNodeMock).pipe(
      delay(mockEndpointDelay)
    );
  }

  userLogin(_csrfToken: string): Observable<RemoteDiagnosticsSession> {
    return of({ session: mockSessionId }).pipe(delay(mockEndpointDelay));
  }

  userLoginCSRF(_csrfToken: string): Observable<RemoteDiagnosticsSession> {
    return of({ session: mockSessionId }).pipe(delay(mockEndpointDelay));
  }

  openDiagStackConnection(
    _gatewaySerial: string,
    _reason: string
  ): Observable<RemoteDiagnosticsSeesionGuid> {
    return of({
      data: mockSessionGuid,
    }).pipe(delay(mockEndpointDelay));
  }

  closeDiagStackConnection(_gatewaySerial: string): Observable<RemoteDiagnosticsSessionClosed> {
    return of({
      success: true,
    }).pipe(delay(mockEndpointDelay));
  }

  diagnosticServiceDtcActive(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLinkName: string,
    _vciName: string,
    _resource: string
  ): Observable<DiagStackGenericResponse> {
    return of(RemoteDiagnosticsCloudApiMockResponses.activeMock).pipe(delay(mockEndpointDelay));
  }

  diagnosticProductMatrix(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLinkName: string,
    _resource: string,
    _vciName: string
  ): Observable<ProductMatrixStructure> {
    return of(RemoteDiagnosticsCloudApiMockResponses.productMatrixMock).pipe(
      withLatestFrom(this.activatedRoute.queryParams),
      map(([productMatrix, queryParams]) => {
        return {
          ...productMatrix,
          structure: productMatrix.structure.map((structure) => ({
            ...structure,
            ViessmannIdentificationNumber: queryParams.deviceSerial ?? 'serial',
          })),
        };
      }),
      delay(mockEndpointDelay)
    );
  }

  diagnosticInfoUseCases(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLink: string,
    _vciName: string,
    _resource: string,
    _vinNumber: string
  ): Observable<UseCases> {
    return of(RemoteDiagnosticsCloudApiMockResponses.useCases).pipe(delay(mockEndpointDelay));
  }

  diagnosticServiceLogicalLinkList(
    _hostAndPath: string,
    _xAuthToken: string
  ): Observable<string[]> {
    return of(true).pipe(
      withLatestFrom(this.activatedRoute.queryParams),
      map(([_, queryParams]) => {
        switch (queryParams.deviceSerial) {
          case '7372062-vitocharge-serial':
          case '7745667-device-serial':
            return [RemoteDiagnosticsCloudApiMockResponses.listMocked.LL_0024_EMCU_CAN01];
          //LL_0024_EMCU_CAN01
          case '7690296-device-serial':
            return [RemoteDiagnosticsCloudApiMockResponses.listMocked.LL_0002_HMU_CAN01];
          case '7725237-device-serial':
          case '7725048-device-serial':
            return [RemoteDiagnosticsCloudApiMockResponses.listMocked.LL_0002_HMU_CAN01];
          case '7733737-device-serial':
          case '7727496-device-serial':
          case '7733738-device-serial':
          case '7733747-device-serial':
          case '7720533-device-serial':
            return [RemoteDiagnosticsCloudApiMockResponses.listMocked.LL_0020_HPMU_CAN01];
          case '7892763-device-serial':
          case '7372760-ventilation-serial':
            return [RemoteDiagnosticsCloudApiMockResponses.listMocked.LL_0016_VCU_CAN01];
          default:
            if (environment.env !== 'production') {
              console.log(
                `Logical link list response for ${queryParams.deviceSerial} is missing. Check mocked logical link list.`
              );
            }
        }
      }),
      delay(mockEndpointDelay)
    );
  }

  diagnosticServiceAvailableVci(
    _hostAndPath: string,
    _xAuthToken: string
  ): Observable<RemoteDiagnosticsVciAvailable[]> {
    return of(RemoteDiagnosticsCloudApiMockResponses.availableMock).pipe(delay(mockEndpointDelay));
  }

  setupInfo(_hostAndPath: string, _xAuthToken: string): Observable<SetupInfo> {
    return of(RemoteDiagnosticsCloudApiMockResponses.setupInfoMock).pipe(delay(mockEndpointDelay));
  }

  scanTopology(
    _hostAndPath: string,
    _xAuthToken: string,
    _mainLinkName: string,
    _resource: string,
    _vciName: string
  ): Observable<DiagnosticsStackGenericResponse<DiagnosticsStackDeviceMainStructure[]>> {
    return of(true).pipe(
      withLatestFrom(this.activatedRoute.queryParams),
      map(([_, queryParams]) => {
        switch (queryParams.deviceSerial) {
          case '7372062-vitocharge-serial':
          case '7745667-device-serial':
            return BusTopologyPhaseOne_vitochargeVx3;
          case '7690296-device-serial':
          case '7733738-device-serial':
          case '7733747-device-serial':
            return BusTopologyPhaseOne_vitodens300;
          case '7725237-device-serial':
          case '7725048-device-serial':
            return BusTopologyPhaseOne_vitovalorPT2;
          case '7733737-device-serial':
          case '7727496-device-serial':
          case '7720533-device-serial':
            return BusTopologyPhaseOne_vitocal222;
          case '7892763-device-serial':
          case '7372760-ventilation-serial':
            return BusTopologyPhaseOne_viair;
          default:
            if (environment.env !== 'production') {
              console.log(
                `Topology response for ${queryParams.deviceSerial} is missing. Check mocked topologies.`
              );
            }
        }
      }),
      delay(mockEndpointDelay)
    );
  }

  vddList(
    _hostAndPath: string,
    _xAuthToken: string,
    logicalLink: string,
    _vciName?: string,
    _resource?: string
  ): Observable<RemoteDiagnosticsGetVddListByComChannelGeneric> {
    try {
      const mockedVddList: LogicalLinkVddList = new LogicalLinkVdds[logicalLink]();

      return of(mockedVddList.vddList()).pipe(delay(mockEndpointDelay));
    } catch {
      if (environment.env !== 'production') {
        console.log(
          `Vdd list for  ${logicalLink} is missing. Check mocked-vdd-list-for-LL/index.ts exports`
        );
      }
      return of(null);
    }
  }

  diagnosticServiceDataRead(
    _hostAndPath: string,
    xAuthToken: string,
    logicalLinkName: string,
    _resource: string,
    _vciName: string,
    vdd: string,
    cycle: number = 0
  ): Observable<string> {
    // simulate the response on web socket if the connection exist
    if (cycle === 0) {
      cycle = undefined;
    }
    // sanity check - we dont want to have cycle too often
    if (Number.isInteger(cycle) && cycle < 1000) {
      cycle = 1000;
    }
    const requestId = this.remoteDiagnosticsHelpersService.makeId();
    if (this.remoteDiagnosticsHelpersService.subject) {
      const timerSubscription = timer(0, cycle).subscribe((value) => {
        const cycleValue = value + 1;
        this.remoteDiagnosticsHelpersService.subject.next({
          data:
            'a["MESSAGE\ndestination:/topic/results\ncontent-type:application/json\nsubscription:sub-0\nmessage-id:y9v5Uufg-5459\ncontent-length:1982\n\n' +
            JSON.stringify(
              JSON.stringify(
                this.remoteDiagnosticsHelpersService.getMockedDataReadRunningValue(
                  requestId,
                  null,
                  vdd,
                  cycleValue,
                  1,
                  logicalLinkName
                )
              )
            ) +
            '\u0000"]"',
        });
        new Observable((subscriber) => {
          if (!this.mockWebSocketResponseAlwaysError) {
            subscriber.next({
              data:
                'a["MESSAGE\ndestination:/topic/results\ncontent-type:application/json\nsubscription:sub-0\nmessage-id:y9v5Uufg-5459\ncontent-length:1982\n\n' +
                JSON.stringify(
                  JSON.stringify(
                    this.remoteDiagnosticsHelpersService.getMockedDataReadSuccessValue(
                      requestId,
                      null,
                      vdd,
                      cycleValue,
                      2,
                      logicalLinkName
                    )
                  )
                ) +
                '\u0000"]"',
            });
          } else {
            subscriber.next({
              data:
                'a["MESSAGE\ndestination:/topic/results\ncontent-type:application/json\nsubscription:sub-0\nmessage-id:y9v5Uufg-5459\ncontent-length:1982\n\n' +
                JSON.stringify(
                  JSON.stringify(
                    this.remoteDiagnosticsHelpersService.getMockedDataReadFailureValue(
                      requestId,
                      cycleValue,
                      1
                    )
                  )
                ) +
                '\u0000"]"',
            });
          }
          subscriber.complete();
        })
          .pipe(delay(this.mockWebSocketDelay))
          .subscribe((text) => this.remoteDiagnosticsHelpersService.subject.next(text));
      });

      const vddSimulator: VddSimulator = {
        id: requestId,
        subscription: timerSubscription,
        authToken: xAuthToken,
      };

      this.vddWsSimulators.push(vddSimulator);
    }

    return of(requestId).pipe(delay(this.mockEndpointDelay));
  }

  diagnosticServiceCancelCycle(
    _hostAndPath: string,
    _xAuthToken: string,
    requestId: string
  ): Observable<string> {
    this.vddWsSimulators
      .filter((vdd) => vdd.id === requestId)
      .forEach((vddSimulator) => vddSimulator.subscription.unsubscribe());

    return of('The cycle for the given id is broken: ' + requestId).pipe(
      delay(this.mockEndpointDelay)
    );
  }

  diagnosticServiceDataReadSync(
    _hostAndPath: string,
    _xAuthToken: string,
    logicalLinkName: string,
    _resource: string,
    _vciName: string,
    vddName: string,
    _cycle: number = 0
  ): Observable<RemoteDiagnosticsSuccessResponseData> {
    try {
      vddName = BackupSeparator.removeBackupSeparator(vddName);
      const mockedVdd: MockedVdd = new Vdds[vddName]();
      return of(mockedVdd.readSingleSync(logicalLinkName)).pipe(delay(mockEndpointDelay));
    } catch {
      if (environment.env !== 'production') {
        console.log(
          `Vdd response for ${vddName} missing. Check mocked-vdds/index.ts exports. ViessmannIdentificationNumber will be used as a mocked backup.`
        );
      }
      return of(new Vdds['ViessmannIdentificationNumber']().readSingleSync(logicalLinkName)).pipe(
        delay(mockEndpointDelay)
      );
    }
  }

  diagnosticServiceDataWrite(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLinkName: string,
    _resource: string,
    _vciName: string,
    _singleReadDataParameter: any
  ): Observable<any> {
    return of('').pipe(delay(mockEndpointDelay));
  }

  connectResponseWebSocketSubject(_hostAndPath: string, _xAuthToken: string): Subject<any> {
    return this.remoteDiagnosticsHelpersService.subject;
  }

  diagnosticInfoVddMetadata(
    _hostAndPath: string,
    _xAuthToken: string,
    _resource: string,
    _vciName: string,
    vddName: string,
    _logicalLink: string
  ): Observable<RemoteDiagnosticsVddRootMetadataResponse> {
    try {
      vddName = BackupSeparator.removeBackupSeparator(vddName);
      const mockedVdd: MockedVdd = new Vdds[vddName]();
      return of(mockedVdd.metadata()).pipe(delay(mockEndpointDelay));
    } catch {
      if (environment.env !== 'production') {
        console.log(
          `Vdd metadata for ${vddName} missing. Check mocked-vdds/index.ts exports. ViessmannIdentificationNumber will be used as a mocked backup.`
        );
      }
      return of(new Vdds['ViessmannIdentificationNumber']().metadata()).pipe(
        delay(mockEndpointDelay)
      );
    }
  }

  diagnosticInfoVddStructure(
    _hostAndPath: string,
    _xAuthToken: string,
    _resource: string,
    _vciName: string,
    vddName: string,
    _logicalLink: string
  ): Observable<DiagnosticsStackGenericResponse<RemoteDiagnosticsVddRootStructureResponse[]>> {
    try {
      vddName = BackupSeparator.removeBackupSeparator(vddName);
      const mockedVdd: MockedVdd = new Vdds[vddName]();
      return of(mockedVdd.structure()).pipe(delay(mockEndpointDelay));
    } catch {
      if (environment.env !== 'production') {
        console.log(
          `Vdd structure for ${vddName} missing. Check mocked-vdds/index.ts exports. ViessmannIdentificationNumber will be used as a mocked backup.`
        );
      }
      return of(new Vdds['ViessmannIdentificationNumber']().structure()).pipe(
        delay(mockEndpointDelay)
      );
    }
  }

  diagnosticServiceForwardLogicalLinkOpen(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLinkName: string,
    _mainLogicalLinkName: string,
    _vciName: string,
    _resource: string,
    _sendTesterPresent: boolean = false,
    _ecuVariantName?: string,
    _appInsightsLogText?: string
  ): Observable<string> {
    return of('').pipe(delay(mockEndpointDelay));
  }

  diagnosticInfoUseCaseVddList(
    _hostAndPath: string,
    _xAuthToken: string,
    _logicalLink: string,
    _vciName: string,
    _resource: string,
    _vinNumber: string,
    useCase: string,
    useCaseType: string
  ): Observable<UseCaseVddList> {
    return of(true).pipe(
      map(() => {
        const result = {
          usecaseName: useCase,
          usecaseType: useCaseType,
          structure: [],
        };

        switch (useCase) {
          case 'Network':
            result.structure = [
              {
                vdd: 'InfoDtcList',
              },
              {
                vdd: 'StatusDtcHistory',
              },
              {
                vdd: 'StatusDtcList',
              },
            ];
            break;
          case 'System':
            result.structure = [
              {
                vdd: 'BusIdentification',
              },
              {
                vdd: 'InfoDtcList',
              },
              {
                vdd: 'StatusDtcHistory',
              },
              {
                vdd: 'StatusDtcList',
              },
            ];
            break;
          case 'Hydraulic':
            result.structure = [
              {
                vdd: 'WaterPressureSensor',
              },
            ];
            break;
          case 'General':
          default:
            result.structure = [
              {
                vdd: 'BusIdentification',
              },
              {
                vdd: 'InfoDtcList',
              },
              {
                vdd: 'StatusDtcHistory',
              },
              {
                vdd: 'StatusDtcList',
              },
              {
                vdd: 'WaterPressureSensor',
              },
              {
                vdd: 'FlowTemperatureSensor',
              },
            ];
            break;
        }

        return result;
      })
    );
  }
}
